// @ts-check

import { defineCustomElement, h, provide, useHost } from "vue";
import { HOST_INJECTION_KEY } from "#ce/composables/useHostElement";
import { MARKER_PROPS, withMarkerCleanupHook, withMountReadyHook } from "./mount-ready";
import { registerCustomElement } from "./registration";
import { enrichProps, isFormAssociated } from "./utils";

// Glob-free by design: the eager apps glob lives in framework/eager.js and the
// lazy catalog glob in framework/catalog.js.

/**
 * @typedef {{ component: import("vue").DefineComponent, plugins: import("vue").Plugin[] }} AppSetup
 * @typedef { (props: import("vue").ComponentObjectPropsOptions) => AppSetup } AppSetupFunction
 * @typedef {{ setup: AppSetupFunction }} App
 */

/**
 * Marks the app component instance with the wrapper's host element, which is
 * what makes `renderSlot` emit a native `<slot>` for the app's slot outlets.
 * Runs after the app's setup, so `inject` there still resolves through the
 * wrapper's provides.
 *
 * @param {import("vue").VNode} vnode - The app component's vnode.
 */
function adoptWrapperHost(vnode) {
  const instance = vnode.component;
  instance.ce = instance.parent.ce;
}

/**
 * Registers an app custom element.
 *
 * @param {App} app - The app's main module (must export a setup function).
 * @param {string} fileName - The app directory name (e.g. "SpFinderOmnisearch").
 * @param {import("vue").ComponentObjectPropsOptions} props - Default values for the component props.
 */
export function registerAppComponent(app, fileName, props) {
  const def = prepareAppDef(app, fileName, props);

  // @ts-ignore
  const elementConstructor = defineCustomElement(def);
  registerCustomElement(fileName, elementConstructor, { formAssociated: isFormAssociated(def) });
}

/**
 * Prepares the custom-element definition for an app.
 * These custom elements are used to wrap Vue 3 components in a custom element.
 * That way we can use the full power of Vue 3 components, including a store etc. in a custom element.
 *
 * Shared def preparation: the eager path registers the result directly, the
 * lazy loader resolves it through `defineAsyncComponent`. Note the app's
 * `main.setup()` runs here — at def-preparation time — so under the lazy build
 * it runs when the chunk arrives, not at boot.
 *
 * @param {App} main - The main object of the app. It must contain a setup function that returns the component and plugins.
 * @param {string} fileName - The file name of the app.
 * @param {import("vue").ComponentObjectPropsOptions} props - Default values for the component props.
 *
 * @returns {import("vue").DefineComponent}
 */
export function prepareAppDef(main, fileName, props) {
  const { component, plugins } = main.setup(props);

  const enrichedProps = enrichProps(component, fileName, props);

  const setup = () => {
    // Native useHost() only binds on the custom element root. The app component
    // runs as a child of this wrapper (see render below), so provide the host
    // here for the host based composables (useExpose, useForm, ...).
    provide(HOST_INJECTION_KEY, useHost());
  };

  /**
   * @type {string[]}
   */
  // @ts-ignore
  const emits = component.emits ?? [];

  const render = ({ $props, $emit }) => {
    const domEventName = (eventName) => `on${eventName[0].toUpperCase()}${eventName.slice(1)}`;
    const events = Object.fromEntries(
      emits.map((eventName) => [domEventName(eventName), (payload) => $emit(eventName, payload)]),
    );

    // The marker props are the wrapper's own concern — forwarding them would
    // put them in the app component's $attrs and back onto its root element.
    const forwarded = { ...$props };
    for (const marker of Object.keys(MARKER_PROPS)) {
      delete forwarded[marker];
    }

    return h(component, {
      ...forwarded,
      ...events,
      onVnodeBeforeMount: adoptWrapperHost,
    });
  };

  // @ts-ignore
  return {
    ...component,
    props: { ...enrichedProps, ...MARKER_PROPS },
    // The wrapper's attrs are the host's undeclared attributes, which already sit on the host.
    // Falling through, they would reach the app component's root inside the shadow root.
    inheritAttrs: false,
    setup,
    render,
    // Plugins install on the app that owns this element.
    configureApp(app) {
      component.configureApp?.(app);
      plugins?.forEach((plugin) => app.use(plugin));
    },
    // The app component runs as a render-created child (see render above) and
    // runs its own lifecycle hooks on the child instance — merging its
    // `mounted` here would invoke it a second time, with the wrapper as
    // `this`. The wrapper's own lifecycle hooks are only the marker hooks.
    mounted: withMountReadyHook({}),
    beforeUnmount: withMarkerCleanupHook({}),
  };
}
